<svelte:options namespace="svg" />
<script lang="ts">
  /** THE ROUTINE AS A GRAPH (NPC life, 10-23 — the Chair's reference frame of a ring of tasks: "The dot in the graph is where the npc is in
   *  transit between states" · "the branching option around the node of discovery … that's the transit time between routine tasks").
   *  Drawn into the trace's svg: the steps as nodes (an in pin, an out pin, the step it is on lit and tabbed NOW), the wires between them
   *  one closed circuit; THE DOT runs along the wire into the step it is walking to (or rests at the step it is at); each DISCOVERY a knot
   *  on the wire out of the step it interrupted, its options branching round it (coloured by the need each serves, the chosen bright).
   *  `mini`: the same graph small (the 75 % / 25 % trace) — no labels, the knots only. */
  import { along, type LoopNode, type LoopWire } from '../loopLayout'
  export interface Knot { at: string; wire: number; f: number; chose: string | null; tab: string; strands: { c: string; chosen: boolean; title: string; s: number }[] }
  let { nodes, wires, cur = -1, transit = false, knots = [], mini = false, nowTab = '', selected = null, onpick, learned = [] }:
    { nodes: LoopNode[]; wires: LoopWire[]; cur?: number; transit?: boolean; knots?: Knot[]; mini?: boolean; nowTab?: string; selected?: string | null; onpick?: (at: string) => void; learned?: string[] } = $props()

  const N = $derived(nodes.length)
  const into = $derived(cur >= 0 && N ? (cur - 1 + N) % N : -1)
  /** a knot's place and the way its options open (away from the wire, toward the room above it — below it near the top) */
  const placed = $derived(knots.map((k) => {
    const w = wires[k.wire]; if (!w) return null
    const p = along(w, k.f), a = along(w, Math.max(0, k.f - 0.03)), b = along(w, Math.min(1, k.f + 0.03))
    let tx = b[0] - a[0], ty = b[1] - a[1]; const tl = Math.hypot(tx, ty) || 1; tx /= tl; ty /= tl
    let nx = -ty, ny = tx; if (ny > 0 === p[1] > 80) { nx = -nx; ny = -ny }
    const base = Math.atan2(ny, nx), n = k.strands.length
    const strands = k.strands.map((s, i) => {
      const a0 = base + (n > 1 ? (i / (n - 1) - 0.5) * 1.6 : 0), len = 34 + 58 * s.s
      const ex = p[0] + Math.cos(a0) * len, ey = p[1] + Math.sin(a0) * len, cx = p[0] + Math.cos(base) * len * 0.45, cy = p[1] + Math.sin(base) * len * 0.45
      return { ...s, d: `M${p[0].toFixed(1)},${p[1].toFixed(1)} Q${cx.toFixed(1)},${cy.toFixed(1)} ${ex.toFixed(1)},${ey.toFixed(1)}`, ex, ey }
    })
    return { ...k, p, strands }
  }).filter((x) => !!x))
  const sw = $derived(mini ? 2.2 : 1)
</script>

<g class="loop" class:mini>
  <!-- the wires: the circuit, the transit into the step it is walking to brighter -->
  {#each wires as w, i (i)}
    <path d={w.d} class="wglow" class:hot={i === into && transit} stroke-width={6 * sw} />
    <path d={w.d} class="wcore" class:hot={i === into && transit} stroke-width={1.4 * sw} />
  {/each}
  <!-- the discoveries: a knot on the wire out of the step it interrupted, its options round it -->
  {#each placed as k (k!.at)}
    <g class="knot" class:sel={k!.at === selected} role="button" tabindex="0" aria-label={`A discovery at ${k!.tab}: ${k!.chose ?? 'nothing chosen'} — open its tree`}
      onclick={() => onpick?.(k!.at)} onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onpick?.(k!.at) } }}>
      {#if !mini}{#each k!.strands as s}<path d={s.d} stroke={s.c} class="strand" class:chosen={s.chosen}><title>{s.title}</title></path>{/each}{/if}
      <circle cx={k!.p[0]} cy={k!.p[1]} r={10 * sw} class="kring" />
      <circle cx={k!.p[0]} cy={k!.p[1]} r={4.5 * sw} class="knode" />
      {#if !mini}<text x={k!.p[0] + 14} y={k!.p[1] - 10} class="ktab">{k!.tab}</text>{/if}
      <title>{k!.tab} — {k!.chose ? `chose ${k!.chose}` : 'chose nothing — back on its routine'}</title>
    </g>
  {/each}
  <!-- the steps -->
  {#each nodes as n, i (n.n)}
    <g class="node" class:now={i === cur} class:learned={learned.includes(n.n)}>
      <rect x={n.x} y={n.y} width={n.w} height={n.h} rx="3" />
      <polygon points={`${n.x + 7},${n.y + n.h / 2} ${n.x + 12},${n.y + n.h / 2 - 3.5} ${n.x + 12},${n.y + n.h / 2 + 3.5}`} class="pin" />
      <rect x={n.x + n.w - 11} y={n.y + n.h / 2 - 3} width="6" height="6" class="pin" />
      {#if !mini}<text x={n.x + n.w / 2 + 1} y={n.y + n.h / 2 + 4} class="lbl" class:long={n.label.length > 12}>{n.label}</text>{/if}
      {#if learned.includes(n.n) && i !== cur && !mini}
        <rect x={n.x + 4} y={n.y - 11} width="46" height="9" rx="1.5" class="ltab" />
        <text x={n.x + 9} y={n.y - 4} class="tabt">LEARNED</text>
      {/if}
      {#if i === cur && !mini}
        <rect x={n.x + 4} y={n.y - 11} width={Math.max(46, nowTab.length * 5.6 + 10)} height="9" rx="1.5" class="tab" />
        <text x={n.x + 9} y={n.y - 4} class="tabt">{nowTab}</text>
      {/if}
      <title>{n.n}{i === cur ? ' — now' : ''}{learned.includes(n.n) ? ' — learned: a choice that stuck' : ''}</title>
    </g>
  {/each}
  <!-- THE DOT: on the wire into the step it is walking to, or resting at the one it is at -->
  {#if cur >= 0 && N}
    {#if transit && wires[into]}
      <circle r={13 * sw} class="dring"><animateMotion dur="2.4s" repeatCount="indefinite" path={wires[into].d} /></circle>
      <circle r={5 * sw} class="dot"><animateMotion dur="2.4s" repeatCount="indefinite" path={wires[into].d} /></circle>
    {:else}
      <circle cx={nodes[cur].in[0]} cy={nodes[cur].in[1]} r={13 * sw} class="dring" />
      <circle cx={nodes[cur].in[0]} cy={nodes[cur].in[1]} r={5 * sw} class="dot" />
    {/if}
  {/if}
</g>

<style>
  .wglow { fill: none; stroke: #22d3ee; opacity: .16; stroke-linecap: round; stroke-linejoin: round; }
  .wcore { fill: none; stroke: #9fe8f5; opacity: .85; stroke-linecap: round; stroke-linejoin: round; }
  .wglow.hot { opacity: .42; }
  .wcore.hot { stroke: #e6fbff; }
  .node rect:first-child { fill: #08130f; stroke: #1f9a63; stroke-width: 1.3; }
  .node.now rect:first-child { fill: #0f3324; stroke: #5dfcb0; stroke-width: 1.8; filter: drop-shadow(0 0 6px rgba(46, 229, 157, .65)); }
  .pin { fill: #c43c3c; }
  .node .pin:first-of-type { fill: #9fe8f5; }
  .lbl { font: 700 10.5px var(--display); fill: #e8fff4; text-anchor: middle; letter-spacing: .04em; }
  .lbl.long { font-size: 9px; letter-spacing: .02em; }
  .tab { fill: #19c37d; }
  .ltab { fill: #f5c518; }
  .node.learned rect:first-child { stroke: #f5c518; }
  .tabt { font: 700 7px var(--mono); fill: #04130c; letter-spacing: .04em; }
  .knot { cursor: pointer; outline: none; }
  .kring { fill: none; stroke: #2ee59d; stroke-width: 1; opacity: .55; }
  .knode { fill: #0f3324; stroke: #5dfcb0; stroke-width: 1.6; }
  .knot.sel .kring { stroke: #fff; opacity: .9; stroke-width: 1.4; }
  .knot:focus-visible .kring, .knot:hover .kring { stroke: #22d3ee; opacity: 1; }
  .ktab { font: 8px var(--mono); fill: #9fe8f5; }
  .strand { fill: none; stroke-width: 1.4; opacity: .7; stroke-linecap: round; filter: drop-shadow(0 0 2px currentColor); }
  .strand.chosen { stroke-width: 4; opacity: 1; }
  .dot { fill: #e6fbff; filter: drop-shadow(0 0 5px rgba(34, 211, 238, .95)); }
  .dring { fill: none; stroke: #2ee59d; stroke-width: 1.2; opacity: .75; }
  .mini .node rect:first-child { stroke-width: 2.6; }
</style>
